/**
 * Signal Condition Band — Signal Framework surface on POR read views.
 *
 * Two renderings, gated by the signal_response module (canon 23 v2 §6.4,
 * SIG-2):
 *
 *   ON  — the honest watch band + unified signal log (SignalLog). No
 *         condition letter is rendered or selectable anywhere; exposure is
 *         derived (migration 673), never chosen.
 *   OFF — the legacy A/B/C/D display below, unchanged. Its Reclassify
 *         modal is legacy-only and slated for removal with the flag window
 *         (PRD SIG-2); do not extend it.
 *
 * Canon 23 §4: B/C/D are retrospective verdicts, not present-tense
 * self-classification. Do not re-mount the legacy band as ambient
 * dashboard status.
 */

import { useState } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import {
  POR_CONDITIONS,
  POR_CONDITION_ORDER,
  type AssembledPor,
  type PorCondition,
} from '../../types/plan-of-record';
import { POR_CONDITION_SET_LINE, porConditionSetAllowed, setPorCondition } from '../../lib/planOfRecordService';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useModuleGateForWorkspace } from '../../lib/moduleGate';
import { SignalLog } from '../signals/SignalLog';
import { CONDITION_ACCENT, PorModal, GhostButton, PrimaryButton } from './porUi';
import { PorNarrativeMarkdown } from './PorNarrativeMarkdown';

export function ConditionBand({
  assembled,
  refresh,
  readOnly = false,
}: {
  assembled: AssembledPor;
  refresh?: () => Promise<void>;
  /** Read views (Synopsis / Sponsor Brief) show the condition without the reclassify affordance. */
  readOnly?: boolean;
}) {
  // Gate on the RECORD's workspace, not the session's (demo mode / active
  // tenant can differ from the workspace this PoR belongs to).
  const signalV2 = useModuleGateForWorkspace(assembled.record.workspace_id, 'signal_response');
  if (signalV2) {
    return (
      <SignalLog
        workspaceId={assembled.record.workspace_id}
        porId={assembled.id}
        canAct={!readOnly}
      />
    );
  }
  return <LegacyConditionBand assembled={assembled} refresh={refresh} readOnly={readOnly} />;
}

function LegacyConditionBand({
  assembled,
  refresh,
  readOnly = false,
}: {
  assembled: AssembledPor;
  refresh?: () => Promise<void>;
  readOnly?: boolean;
}) {
  const active = assembled.record.current_condition;
  const activeMeta = POR_CONDITIONS[active];
  const [reclassifying, setReclassifying] = useState(false);
  // set_por_condition takes an admin or owner of the PoR's workspace (735):
  // Reclassify and its condition buttons are drawn only for them (§2.8).
  const can = useUserStore((st) => st.can);
  const mayReclassify =
    !readOnly &&
    porConditionSetAllowed({
      can,
      porWorkspaceId: assembled.record.workspace_id,
      currentWorkspaceId: getCurrentWorkspaceId(),
    });

  return (
    <section>
      {/* Failure-mode banner */}
      {activeMeta.isFailureMode && (
        <div className="mb-3 flex items-start gap-3 border border-luminous-coral-200 bg-luminous-coral-50 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-luminous-coral-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-luminous-coral-900">
              Condition {active} — {activeMeta.name}
            </p>
            <p className="text-xs text-luminous-coral-800 mt-0.5">{activeMeta.behavioralImplication}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)]">Signal Condition</h3>
        {mayReclassify ? (
          <button
            onClick={() => setReclassifying(true)}
            className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
          >
            Reclassify
          </button>
        ) : !readOnly ? (
          <span className="text-xs text-[var(--text-ter)]" data-testid="por-condition-refused">
            {POR_CONDITION_SET_LINE}
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {POR_CONDITION_ORDER.map((code) => {
          const meta = POR_CONDITIONS[code];
          const isActive = code === active;
          const accent = isActive
            ? meta.isFailureMode
              ? CONDITION_ACCENT.failureActive
              : CONDITION_ACCENT.healthyActive
            : CONDITION_ACCENT.inactive;
          return (
            <div key={code} className={` border p-4 ${accent}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-lg font-bold">{code}</span>
                {isActive ? (
                  meta.isFailureMode ? (
                    <AlertTriangle className="h-4 w-4" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )
                ) : (
                  <span className="text-[10px] font-mono uppercase tracking-wide opacity-60">
                    {meta.isFailureMode ? 'failure mode' : 'reference'}
                  </span>
                )}
              </div>
              <p className={`text-sm font-semibold mt-1 ${isActive ? '' : 'text-[var(--text-ter)]'}`}>{meta.name}</p>
              <p className={`text-xs mt-1 leading-snug ${isActive ? '' : 'text-[var(--text-ter)]'}`}>
                {meta.definition}
              </p>
            </div>
          );
        })}
      </div>

      {/* Active condition rationale — the "why we are here" line */}
      <div className="mt-3 bg-[var(--surface)] dark:bg-[color-mix(in_srgb,var(--bg)_70%,transparent)] border border-[var(--rule)] dark:border-[var(--rule)] px-4 py-3">
        <div className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-2">
          Current rationale
        </div>
        <PorNarrativeMarkdown
          source={assembled.record.condition_rationale ?? ''}
          emptyHint="No rationale captured for this condition yet."
        />
      </div>

      {mayReclassify && reclassifying && (
        <ReclassifyModal
          assembled={assembled}
          onClose={() => setReclassifying(false)}
          onDone={async () => {
            setReclassifying(false);
            await refresh?.();
          }}
        />
      )}
    </section>
  );
}

function ReclassifyModal({
  assembled,
  onClose,
  onDone,
}: {
  assembled: AssembledPor;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const current = assembled.record.current_condition;
  const [selected, setSelected] = useState<PorCondition>(current);
  const [linkedSignalEventId, setLinkedSignalEventId] = useState<string>('');
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = rationale.trim().length > 0 && selected !== current;

  const handleConfirm = async () => {
    if (rationale.trim().length === 0) {
      setError('A rationale is required — it cannot be saved empty.');
      return;
    }
    if (selected === current) {
      setError('Pick a different condition to reclassify.');
      return;
    }
    setBusy(true);
    setError(null);
    const { error: err } = await setPorCondition(
      assembled.id,
      selected,
      rationale.trim(),
      linkedSignalEventId || null,
    );
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  return (
    <PorModal
      title="Reclassify signal condition"
      subtitle="Set by a human with authority — never inferred. This appends a governance event."
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleConfirm} disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Reclassify'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-2 mb-4">
        {POR_CONDITION_ORDER.map((code) => {
          const meta = POR_CONDITIONS[code];
          return (
            <button
              key={code}
              onClick={() => setSelected(code)}
              className={`w-full text-left  border px-3 py-2 transition-colors ${
                selected === code
                  ? meta.isFailureMode
                    ? 'border-luminous-coral-300 bg-luminous-coral-50'
                    : 'border-primary-300 bg-primary-50'
                  : 'border-[var(--rule)] hover:bg-[var(--surface)]'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-sm">{code}</span>
                <span className="text-sm font-medium text-[var(--text-pri)]">{meta.name}</span>
                {code === current && (
                  <span className="text-[10px] font-mono uppercase text-[var(--text-ter)]">current</span>
                )}
              </div>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">{meta.definition}</p>
            </button>
          );
        })}
      </div>

      {assembled.open_signal_events.length > 0 && (
        <div className="mb-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">
            Link to a signal event
          </label>
          <select
            value={linkedSignalEventId}
            onChange={(e) => setLinkedSignalEventId(e.target.value)}
            className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none"
          >
            <option value="">No linked event</option>
            {assembled.open_signal_events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                [{ev.condition}] {ev.description.slice(0, 60)}
              </option>
            ))}
          </select>
        </div>
      )}

      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">
        Rationale <span className="text-luminous-coral-700">*</span>
      </label>
      <textarea
        value={rationale}
        onChange={(e) => {
          setRationale(e.target.value);
          if (error) setError(null);
        }}
        rows={3}
        placeholder="Why this classification? Recorded in the immutable Change Log."
        className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none resize-none"
      />
      {error && <p className="text-xs text-luminous-coral-700 mt-1.5">{error}</p>}
    </PorModal>
  );
}
